import { FacetOption } from '@tailwindComponents/CheckboxFacetGroup';
import { Customer } from './customerTypes';

export const ALL_MARKETS = 'ALL';

export type CustomerHierarchySelection =
  | { kind: 'all' }
  | { kind: 'group'; groupName: string }
  | { kind: 'subNode'; groupName: string; subNodeName: string }
  | { kind: 'independents' }
  | { kind: 'unassigned' };

export interface CustomerFacetState {
  hierarchy: CustomerHierarchySelection;
  channels: Set<string>;
}

export const emptyCustomerFacetState = (): CustomerFacetState => ({
  hierarchy: { kind: 'all' },
  channels: new Set(),
});

export const hasActiveHierarchy = (facets: CustomerFacetState): boolean =>
  facets.hierarchy.kind !== 'all';

export const hasActiveFacets = (facets: CustomerFacetState): boolean =>
  hasActiveHierarchy(facets) || facets.channels.size > 0;

export const matchesMarket = (customer: Customer, market: string): boolean =>
  market === ALL_MARKETS || customer.market === market;

const matchesChannel = (
  customer: Customer,
  facets: CustomerFacetState
): boolean => {
  if (!facets.channels.size) return true;
  return customer.channel !== null && facets.channels.has(customer.channel);
};

const matchesHierarchy = (
  customer: Customer,
  facets: CustomerFacetState
): boolean => {
  const { hierarchy } = facets;
  const target = customer.hierarchy;
  switch (hierarchy.kind) {
    case 'all':
      return true;
    case 'group':
      return (
        target.bucket === 'group' && target.groupName === hierarchy.groupName
      );
    case 'subNode':
      return (
        target.bucket === 'group' &&
        target.groupName === hierarchy.groupName &&
        target.subNodeName === hierarchy.subNodeName
      );
    case 'independents':
      return target.bucket === 'independent';
    case 'unassigned':
      return target.bucket === 'unassigned';
    default:
      return true;
  }
};

export const applyFacets = (
  customers: Customer[],
  facets: CustomerFacetState,
  market: string
): Customer[] =>
  customers.filter(
    (customer) =>
      matchesMarket(customer, market) &&
      matchesChannel(customer, facets) &&
      matchesHierarchy(customer, facets)
  );

export const deriveMarketOptions = (
  customers: Customer[]
): { label: string; value: string }[] => {
  const markets = [...new Set(customers.map((customer) => customer.market))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  return markets.map((market) => ({ label: market, value: market }));
};

export const deriveChannelOptions = (
  customers: Customer[],
  facets: CustomerFacetState,
  market: string
): FacetOption[] => {
  const counts = new Map<string, number>();
  customers.forEach((customer) => {
    if (!matchesMarket(customer, market)) return;
    if (!matchesHierarchy(customer, facets)) return;
    if (customer.channel === null) return;
    counts.set(customer.channel, (counts.get(customer.channel) ?? 0) + 1);
  });
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: value, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const toggleChannel = (
  facets: CustomerFacetState,
  value: string
): CustomerFacetState => {
  const channels = new Set(facets.channels);
  if (channels.has(value)) {
    channels.delete(value);
  } else {
    channels.add(value);
  }
  return { ...facets, channels };
};

export const setSingleChannel = (
  facets: CustomerFacetState,
  value: string | null
): CustomerFacetState => ({
  ...facets,
  channels: value === null ? new Set() : new Set([value]),
});

export const selectAll = (facets: CustomerFacetState): CustomerFacetState => ({
  ...facets,
  hierarchy: { kind: 'all' },
});

export const selectGroup = (
  facets: CustomerFacetState,
  groupName: string
): CustomerFacetState => ({
  ...facets,
  hierarchy: { kind: 'group', groupName },
});

export const selectSubNode = (
  facets: CustomerFacetState,
  groupName: string,
  subNodeName: string
): CustomerFacetState => ({
  ...facets,
  hierarchy: { kind: 'subNode', groupName, subNodeName },
});

export const selectIndependents = (
  facets: CustomerFacetState
): CustomerFacetState => ({
  ...facets,
  hierarchy: { kind: 'independents' },
});

export const selectUnassigned = (
  facets: CustomerFacetState
): CustomerFacetState => ({
  ...facets,
  hierarchy: { kind: 'unassigned' },
});

export interface CustomerHierarchySubNode {
  name: string;
  count: number;
}

export interface CustomerHierarchyGroup {
  name: string;
  avatarCode: string | null;
  count: number;
  subNodes: CustomerHierarchySubNode[];
}

export interface CustomerHierarchyTree {
  total: number;
  groups: CustomerHierarchyGroup[];
  independents: number;
  unassigned: number;
}

export const buildHierarchyTree = (
  customers: Customer[],
  facets: CustomerFacetState,
  market: string
): CustomerHierarchyTree => {
  const inScope = customers.filter(
    (customer) =>
      matchesMarket(customer, market) && matchesChannel(customer, facets)
  );

  const groupOrder: string[] = [];
  const groupTotals = new Map<string, number>();
  const groupAvatars = new Map<string, string | null>();
  const groupSubNodes = new Map<string, Map<string, number>>();
  let independents = 0;
  let unassigned = 0;

  inScope.forEach((customer) => {
    const { hierarchy } = customer;
    if (hierarchy.bucket === 'independent') {
      independents += 1;
      return;
    }
    if (hierarchy.bucket === 'unassigned' || !hierarchy.groupName) {
      unassigned += 1;
      return;
    }
    const { groupName } = hierarchy;
    if (!groupTotals.has(groupName)) {
      groupOrder.push(groupName);
      groupAvatars.set(groupName, hierarchy.groupAvatarCode);
    }
    groupTotals.set(groupName, (groupTotals.get(groupName) ?? 0) + 1);
    if (hierarchy.subNodeName) {
      const subCounts = groupSubNodes.get(groupName) ?? new Map();
      subCounts.set(
        hierarchy.subNodeName,
        (subCounts.get(hierarchy.subNodeName) ?? 0) + 1
      );
      groupSubNodes.set(groupName, subCounts);
    }
  });

  const groups = groupOrder.map((name) => ({
    name,
    avatarCode: groupAvatars.get(name) ?? null,
    count: groupTotals.get(name) ?? 0,
    subNodes: [...(groupSubNodes.get(name) ?? new Map()).entries()]
      .map(([subName, count]) => ({ name: subName, count }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }));

  return {
    total: inScope.length,
    groups,
    independents,
    unassigned,
  };
};
